/**
 * Core Logger Service
 * Provides structured logging with levels, contexts, and performance timing
 */

import type {
  LogEntry,
  LoggableData,
  LoggerConfig,
  PerformanceTimer,
} from '@xanectech/types';
import { LogLevel, LOG_LEVEL_NAMES, LOG_LEVEL_COLORS } from '@xanectech/types';
import { DEFAULT_CONFIG } from './LoggerConfig';

// import { useLogs, createLogs } from '@xanectech/supabase-client';

class LoggerService {
  private config: LoggerConfig;
  private entries: LogEntry[] = [];
  private timers: Map<string, PerformanceTimer> = new Map();
  private context: string | null = null;
  private userId: string | null = null;
  private sessionId: string;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();
  }

  /**
   * Set the current logging context (e.g., component name)
   */
  setContext(context: string | null): void {
    this.context = context;
  }

  /**
   * Set the current user ID for logging
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enable/disable a logging category
   */
  setCategory(category: string, enabled: boolean): void {
    this.config.categories[category] = enabled;
  }

  /**
   * Log a debug message
   */
  debug(
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
  ): void {
    this.log(LogLevel.DEBUG as LogLevel, message, metadata, category);
  }

  /**
   * Log an info message
   */
  info(
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
  ): void {
    this.log(LogLevel.INFO as LogLevel, message, metadata, category);
  }

  /**
   * Log a warning message
   */
  warn(
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
  ): void {
    this.log(LogLevel.WARN as LogLevel, message, metadata, category);
  }

  /**
   * Log an error message
   */
  error(
    message: string,
    error?: Error | Record<string, LoggableData>,
    category?: string,
  ): void {
    const metadata = error instanceof Error ? undefined : error;
    const errorObj = error instanceof Error ? error : undefined;
    this.log(LogLevel.ERROR as LogLevel, message, metadata, category, errorObj);
  }

  /**
   * Start a performance timer
   */
  time(label: string, metadata?: Record<string, LoggableData>): void {
    this.timers.set(label, {
      label,
      startTime: performance.now(),
      context: this.context || undefined,
      metadata,
    });
  }

  /**
   * End a performance timer and log the duration
   */
  timeEnd(
    label: string,
    metadata?: Record<string, LoggableData>,
  ): number | null {
    const timer = this.timers.get(label);
    if (!timer) {
      this.warn(`Timer "${label}" not found`, { label }, 'performance');
      return null;
    }

    const duration = performance.now() - timer.startTime;
    this.timers.delete(label);

    const timerMetadata: Record<string, LoggableData> = {
      duration: Math.round(duration * 100) / 100,
      unit: 'ms',
    };

    if (timer.metadata) {
      Object.assign(timerMetadata, timer.metadata);
    }

    if (metadata) {
      Object.assign(timerMetadata, metadata);
    }

    this.info(`Performance: ${label}`, timerMetadata, 'performance');

    return duration;
  }

  /**
   * Core logging method
   */
  private log(
    level: LogLevel,
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
    error?: Error,
  ): void {
    if (!import.meta.env.VITE_DEV && level === LogLevel.DEBUG) {
      return;
    }

    // Check if logging level is enabled
    if (level < this.config.level) {
      return;
    }

    // Check if category is enabled
    if (category && !this.config.categories[category]) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: this.context || undefined,
      metadata,
      userId: this.userId || undefined,
      sessionId: this.sessionId || undefined,
      error,
    };

    // Store entry
    this.addEntry(entry);

    // Console output
    if (this.config.enableConsole) {
      this.outputToConsole(entry, category);
    }
  }

  /**
   * Add entry to internal storage
   */
  private addEntry(entry: LogEntry): void {
    this.entries.push(entry);

    // Maintain max entries limit
    if (this.entries.length > this.config.maxEntries) {
      this.entries.shift();
    }
  }

  /**
   * Output log entry to console with formatting
   */
  private outputToConsole(entry: LogEntry, category?: string): void {
    const levelName = LOG_LEVEL_NAMES[entry.level];
    const color = LOG_LEVEL_COLORS[entry.level];

    let prefix = '';
    if (this.config.enableTimestamps) {
      const time = new Date(entry.timestamp as string).toLocaleTimeString();
      prefix += `[${time}] `;
    }

    prefix += `%c[${levelName}]`;

    if (entry.context && this.config.enableContext) {
      prefix += ` [${entry.context}]`;
    }

    if (category) {
      prefix += ` [${category}]`;
    }

    const args: (string | Record<string, unknown>)[] = [
      `${prefix} ${entry.message}`,
      `color: ${color}; font-weight: bold`,
    ];

    if (entry.metadata) {
      args.push('\n📋 Metadata:', entry.metadata as Record<string, unknown>);
    }

    if (entry.userId) {
      args.push('\n👤 User:', entry.userId as string);
    }

    if (entry.error) {
      args.push('\n🚨 Error:', {
        message: entry.error.message,
        stack: entry.error.stack,
      });
    }

    // Use appropriate console method
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(...args);
        break;
      case LogLevel.INFO:
        console.info(...args);
        break;
      case LogLevel.WARN:
        console.warn(...args);
        break;
      case LogLevel.ERROR:
        console.error(...args);
        break;
    }
  }

  private outputToSupabase(entry: LogEntry, category?: string): void {
    const levelName = LOG_LEVEL_NAMES[entry.level];
    const color = LOG_LEVEL_COLORS[entry.level];

    let prefix = '';
    if (this.config.enableTimestamps) {
      const time = new Date(entry.timestamp as string).toLocaleTimeString();
      prefix += `[${time}] `;
    }

    prefix += `%c[${levelName}]`;

    if (entry.context && this.config.enableContext) {
      prefix += ` [${entry.context}]`;
    }

    if (category) {
      prefix += ` [${category}]`;
    }

    const args: (string | Record<string, unknown>)[] = [
      `${prefix} ${entry.message}`,
      `color: ${color}; font-weight: bold`,
    ];

    if (entry.metadata) {
      args.push('\n📋 Metadata:', entry.metadata as Record<string, unknown>);
    }

    if (entry.userId) {
      args.push('\n👤 User:', entry.userId as string);
    }

    if (entry.error) {
      args.push('\n🚨 Error:', {
        message: entry.error.message,
        stack: entry.error.stack,
      });
    }

    // Use appropriate console method
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(...args);
        break;
      case LogLevel.INFO:
        console.info(...args);
        break;
      case LogLevel.WARN:
        console.warn(...args);
        break;
      case LogLevel.ERROR:
        console.error(...args);
        break;
    }
  }

  /**
   * Get all log entries
   */
  getEntries(): LogEntry[] {
    return [...this.entries];
  }

  /**
   * Get entries by level
   */
  getEntriesByLevel(level: LogLevel): LogEntry[] {
    return this.entries.filter((entry) => entry.level === level);
  }

  /**
   * Get entries by category from metadata
   */
  getEntriesByCategory(category: string): LogEntry[] {
    return this.entries.filter(
      (entry) => entry.metadata?.category === category,
    );
  }

  /**
   * Clear all log entries
   */
  clear(): void {
    this.entries = [];
    this.timers.clear();
  }

  /**
   * Export logs as JSON
   */
  export(): string {
    return JSON.stringify(
      {
        config: this.config,
        sessionId: this.sessionId,
        userId: this.userId,
        entries: this.entries,
        exportedAt: new Date().toISOString(),
      },
      null,
      2,
    );
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a scoped logger for a specific context
   */
  scope(context: string): ScopedLogger {
    return new ScopedLogger(this, context);
  }
}

/**
 * Scoped Logger for component-specific logging
 */
class ScopedLogger {
  constructor(
    private logger: LoggerService,
    private scopeContext: string,
  ) {}

  debug(
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
  ): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    this.logger.debug(message, metadata, category);
    this.logger.setContext(originalContext);
  }

  info(
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
  ): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    this.logger.info(message, metadata, category);
    this.logger.setContext(originalContext);
  }

  warn(
    message: string,
    metadata?: Record<string, LoggableData>,
    category?: string,
  ): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    this.logger.warn(message, metadata, category);
    this.logger.setContext(originalContext);
  }

  error(
    message: string,
    error?: Error | Record<string, LoggableData>,
    category?: string,
  ): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    this.logger.error(message, error, category);
    this.logger.setContext(originalContext);
  }

  time(label: string, metadata?: Record<string, LoggableData>): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    this.logger.time(label, metadata);
    this.logger.setContext(originalContext);
  }

  timeEnd(
    label: string,
    metadata?: Record<string, LoggableData>,
  ): number | null {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    const result = this.logger.timeEnd(label, metadata);
    this.logger.setContext(originalContext);
    return result;
  }

  performance(operation: string, startTime: number): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    const duration = performance.now() - startTime;
    this.logger.info(
      `Performance: ${operation}`,
      {
        duration: Math.round(duration * 100) / 100,
        unit: 'ms',
      },
      'performance',
    );
    this.logger.setContext(originalContext);
  }

  userAction(action: string, data?: unknown): void {
    const originalContext = this.logger['context'];
    this.logger.setContext(this.scopeContext);
    this.logger.info(
      `User Action: ${action}`,
      data ? { data: data as LoggableData } : undefined,
    );
    this.logger.setContext(originalContext);
  }
}

// Create and export the default logger instance
const logger = new LoggerService();

// Export the service class for custom instances
export {
  logger,
  LoggerService,
  ScopedLogger,
  LogLevel,
  type LogEntry,
  type LoggerConfig,
};

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return fallback ?? 'An unexpected error occurred. Please try again.';
}
